// LTTB (Largest Triangle Three Buckets) algorithm for downsampling time series data
// This preserves the visual shape while reducing data points significantly

export interface DataPoint {
  timestamp: string;
  cumulative_pnl: number;
  total_pnl: number;
  bot_contributions: Record<string, number>;
  originalIndex?: number;
}

export function downsampleLTTB(data: DataPoint[], maxPoints: number): DataPoint[] {
  if (data.length <= maxPoints || maxPoints <= 2) {
    return data.map((point, index) => ({ ...point, originalIndex: index }));
  }

  const bucketSize = (data.length - 2) / (maxPoints - 2);
  const sampledData: DataPoint[] = [];

  // Always include first point
  sampledData.push({ ...data[0], originalIndex: 0 });

  let bucketIndex = 0;
  for (let i = 1; i < maxPoints - 1; i++) {
    const bucketStart = Math.floor(bucketIndex) + 1;
    const bucketEnd = Math.min(Math.floor(bucketIndex + bucketSize) + 1, data.length - 1);

    // Calculate average point of next bucket for triangle area calculation
    let avgX = 0;
    let avgY = 0;
    let avgCount = 0;

    const nextBucketStart = Math.min(bucketEnd, data.length - 1);
    const nextBucketEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, data.length - 1);

    for (let j = nextBucketStart; j < nextBucketEnd; j++) {
      avgX += j;
      avgY += data[j].cumulative_pnl;
      avgCount++;
    }

    if (avgCount > 0) {
      avgX /= avgCount;
      avgY /= avgCount;
    }

    // Find point in current bucket that creates largest triangle
    let maxArea = -1;
    let maxAreaIndex = bucketStart;

    const prevPoint = sampledData[sampledData.length - 1];
    const prevX = prevPoint.originalIndex || 0;
    const prevY = prevPoint.cumulative_pnl;

    for (let j = bucketStart; j < bucketEnd; j++) {
      const currentX = j;
      const currentY = data[j].cumulative_pnl;

      // Calculate triangle area
      const area = Math.abs(
        (prevX - avgX) * (currentY - prevY) - (prevX - currentX) * (avgY - prevY)
      ) * 0.5;

      if (area > maxArea) {
        maxArea = area;
        maxAreaIndex = j;
      }
    }

    sampledData.push({ ...data[maxAreaIndex], originalIndex: maxAreaIndex });
    bucketIndex += bucketSize;
  }

  // Always include last point
  sampledData.push({ ...data[data.length - 1], originalIndex: data.length - 1 });

  return sampledData;
}